import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { UsmcaCheckbox } from "@/components/UsmcaCheckbox";
import { printUsmca } from "@/lib/usmca";
import { printLabelPdf } from "@/lib/printLabel";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const CARRIER_TONE = {
  usps: "bg-blue-50 text-blue-900 border-blue-200",
  canada_post: "bg-red-50 text-red-900 border-red-200",
  ups_shipping: "bg-amber-50 text-amber-900 border-amber-200",
  dhl_express: "bg-yellow-50 text-yellow-900 border-yellow-200",
};

// Destination country → carrier Back Office normally uses (user rule Sep 30, 2026):
// USPS for the USA, Canada Post for Canada and everywhere else.
const defaultCarrierFor = (country) => {
  const c = (country || "").toString().trim().toUpperCase();
  return c === "US" || c === "USA" || c === "UNITED STATES" ? "usps" : "canada_post";
};

// "Ship from Shopify" — full shipping description pop-up (every carrier &
// service for the destination with what it includes), buys the Shopify
// Shipping label, auto-opens the 4×6 print window, saves tracking and moves
// the order to Fulfilled. Shopify's API publishes no prices and accepts no
// signature / insurance add-ons — those labels are bought in Shopify Admin and
// synced back here (user rules Oct 8, 2026).
export default function ShipFromShopifyDialog({ order, open, onOpenChange, onSuccess }) {
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null);
  const [packages, setPackages] = useState([]);
  const [svc, setSvc] = useState(null);
  const [result, setResult] = useState(null);
  const [usmca, setUsmca] = useState(false);
  const [addons, setAddons] = useState({ signature: false, insurance: false, insured_value: "" });
  const [sel, setSel] = useState(null); // {carrier_code, service_code|null}
  const [form, setForm] = useState({
    package_name: "", length: "", width: "", height: "", dim_units: "inches",
    weight_value: "", weight_units: "pounds", notify_customer: true,
  });

  useEffect(() => {
    if (!open) return;
    let ok = true;
    setResult(null);
    setSel(null);
    setAddons({ signature: false, insurance: false, insured_value: "" });
    api.get("/shopify/admin-status").then((r) => { if (ok) setStatus(r.data); }).catch(() => {});
    api.get(`/orders/${order.id}/shopify-label/services`).then((r) => {
      if (!ok) return;
      setSvc(r.data);
      setAddons((a) => ({ ...a, insured_value: r.data?.order_total || "" }));
    }).catch(() => setSvc({ services: [] }));
    api.get("/shipstation/packages").then((r) => {
      if (!ok) return;
      const list = r.data || [];
      setPackages(list);
      if (list[0] && !form.package_name) applyPackage(list[0]);
    }).catch(() => {});
    return () => { ok = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const applyPackage = (p) => {
    if (!p) return;
    setForm((f) => ({ ...f, package_name: p.name, length: p.length, width: p.width, height: p.height,
      dim_units: p.dim_units || "inches", weight_value: p.weight_value, weight_units: p.weight_units || "pounds" }));
  };

  const configured = status?.configured === true;
  const hasShopifyLink = !!(order?.shopify_order_id || "").toString().trim();
  const destCountry = svc?.dest_country || (order?.shipping_address?.country || "").toString().toUpperCase();
  const services = svc?.services || [];
  const byCarrier = useMemo(() => {
    const m = new Map();
    services.forEach((s) => { if (!m.has(s.carrier_code)) m.set(s.carrier_code, { name: s.carrier_name, rows: [] }); m.get(s.carrier_code).rows.push(s); });
    return [...m.entries()];
  }, [services]);
  useEffect(() => {
    if (sel || !byCarrier.length) return;
    const pref = defaultCarrierFor(destCountry);
    const [code] = byCarrier.find(([c]) => c === pref) || byCarrier[0];
    setSel({ carrier_code: code, service_code: null });
  }, [byCarrier, destCountry, sel]);

  const needsAdmin = addons.signature || addons.insurance;
  const dimsOk = Number(form.length) > 0 && Number(form.width) > 0 && Number(form.height) > 0 && Number(form.weight_value) > 0;
  const ready = configured && hasShopifyLink && dimsOk && !!sel && !needsAdmin;
  const isSel = (c, s) => sel?.carrier_code === c && (sel?.service_code || null) === (s || null);

  const submit = async () => {
    setSaving(true);
    if (usmca) printUsmca(order);
    try {
      const body = {
        package_name: form.package_name || null,
        length: Number(form.length), width: Number(form.width), height: Number(form.height), dim_units: form.dim_units,
        weight_value: Number(form.weight_value), weight_units: form.weight_units,
        carrier_code: sel.carrier_code, service_code: sel.service_code || null,
        notify_customer: !!form.notify_customer,
      };
      const r = await api.post(`/orders/${order.id}/shopify-label`, body);
      setResult(r.data);
      toast.success(`Label bought · ${r.data.carrier || "Shopify Shipping"} ${r.data.tracking_number || ""}`.trim());
      printLabelPdf(order.id, { fallbackUrl: r.data.label_url, label: `Label ${order.po_number}` });
      if (typeof onSuccess === "function") { try { await onSuccess(r.data); } catch (_) { /* label is already stored */ } }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Shopify label purchase failed", { duration: 8000 });
    } finally {
      setSaving(false);
    }
  };

  const syncFromAdmin = async () => {
    setSaving(true);
    try {
      const r = await api.post(`/orders/${order.id}/shopify-label/sync`);
      toast.success(`Tracking synced · ${r.data.carrier || ""} ${r.data.tracking_number || ""}`.trim());
      if (typeof onSuccess === "function") { try { await onSuccess(r.data); } catch (_) {} }
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nothing to sync yet", { duration: 8000 });
    } finally {
      setSaving(false);
    }
  };

  const fulfillOnly = async () => {
    setSaving(true);
    if (usmca) printUsmca(order);
    try {
      const r = await api.post(`/orders/${order.id}/ship-from-shopify`);
      toast.success(`Shopify fulfillment created · ${order.po_number}`);
      if (typeof onSuccess === "function") { try { await onSuccess(r.data); } catch (_) {} }
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Shopify fulfillment failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="ship-shopify-dialog">
        <DialogHeader>
          <DialogTitle>Ship from Shopify · {order.po_number}</DialogTitle>
          <DialogDescription>
            Pick the shipping service, buy the Shopify Shipping label (charged to your Shopify account), the 4×6 label prints
            on the label printer, tracking is saved and the order moves to <b>Order Fulfilled</b>.
          </DialogDescription>
        </DialogHeader>

        {!configured && (
          <div className="border border-amber-300 bg-amber-50 text-amber-900 rounded-md p-3 text-sm" data-testid="ship-shopify-not-configured">
            <div className="font-semibold mb-1">⚠ Shopify Admin API not connected yet.</div>
            <div className="text-xs">Add the Shopify credentials to <code className="font-mono">backend/.env</code> (Super Admin).</div>
          </div>
        )}
        {configured && !hasShopifyLink && (
          <div className="border border-red-300 bg-red-50 text-red-900 rounded-md p-3 text-sm" data-testid="ship-shopify-no-link">
            This order has no Shopify order ID (created manually or before the webhook was hooked up). Use <b>ShipStation</b> or <b>Self Checkout</b> instead.
          </div>
        )}

        {result ? (
          <div className="border border-emerald-300 bg-emerald-50 rounded-md p-4 text-sm space-y-2" data-testid="ship-shopify-result">
            <div className="font-semibold text-emerald-900">✓ Label purchased — order moved to Fulfilled</div>
            <div className="font-mono text-xs text-slate-700">{result.carrier || "Shopify Shipping"} · {result.tracking_number || "tracking pending"}</div>
            <div className="text-xs text-slate-600">The print window opened automatically — choose the label printer. Didn't see it?</div>
            <div className="flex gap-2 pt-1 flex-wrap">
              <Button className="bg-slate-900 hover:bg-slate-800 text-white" onClick={() => printLabelPdf(order.id, { fallbackUrl: result.label_url, label: `Label ${order.po_number}` })} data-testid="ship-shopify-print">
                🖨 Print label again
              </Button>
              {result.label_url && (
                <Button variant="outline" onClick={() => window.open(result.label_url, "_blank", "noopener")} data-testid="ship-shopify-open-pdf">Open PDF</Button>
              )}
              {(result.documents || []).filter((d) => d.documentType !== "LABEL" && d.url).map((d) => (
                <Button key={d.url} variant="outline" onClick={() => window.open(d.url, "_blank", "noopener")}>
                  {d.documentType === "CUSTOMS_FORM" ? "📄 Customs form" : d.documentType}
                </Button>
              ))}
              <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="ship-shopify-done">Done</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {/* Box + dimensions */}
            <div className="grid grid-cols-1 md:grid-cols-[1.3fr_2fr] gap-3">
              <div>
                <Label className="text-xs">Box</Label>
                <Select value={form.package_name || ""} onValueChange={(name) => applyPackage(packages.find((p) => p.name === name))}>
                  <SelectTrigger data-testid="shopify-label-package" className="h-10 text-sm font-mono"><SelectValue placeholder="Pick a box…" /></SelectTrigger>
                  <SelectContent>
                    {packages.map((p) => (
                      <SelectItem key={p.id} value={p.name} data-testid={`shopify-label-package-${p.name}`}>
                        {p.name} · {p.length}×{p.width}×{p.height} {p.dim_units === "centimeters" ? "cm" : "in"} · {p.weight_value} {p.weight_units === "pounds" ? "lb" : p.weight_units}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="text-[11px] text-slate-500 mt-1">Presets from Settings → Shipping boxes; values stay editable.</div>
              </div>
              <div className="grid grid-cols-4 gap-2">
                <div><Label className="text-xs">L ({form.dim_units === "centimeters" ? "cm" : "in"})</Label><Input type="number" step="0.1" value={form.length} onChange={(e) => setForm({ ...form, length: e.target.value })} data-testid="shopify-label-length" /></div>
                <div><Label className="text-xs">W</Label><Input type="number" step="0.1" value={form.width} onChange={(e) => setForm({ ...form, width: e.target.value })} data-testid="shopify-label-width" /></div>
                <div><Label className="text-xs">H</Label><Input type="number" step="0.1" value={form.height} onChange={(e) => setForm({ ...form, height: e.target.value })} data-testid="shopify-label-height" /></div>
                <div>
                  <Label className="text-xs">Weight</Label>
                  <div className="flex gap-1">
                    <Input type="number" step="0.1" value={form.weight_value} onChange={(e) => setForm({ ...form, weight_value: e.target.value })} data-testid="shopify-label-weight" />
                    <select className="h-10 rounded-md border border-slate-300 bg-white px-1 text-xs" value={form.weight_units} onChange={(e) => setForm({ ...form, weight_units: e.target.value })} data-testid="shopify-label-weight-units">
                      <option value="pounds">lb</option><option value="ounces">oz</option><option value="kilograms">kg</option><option value="grams">g</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Shipping services — the full description pop-up */}
            <div className="rounded-md border border-slate-200 overflow-hidden" data-testid="shopify-services-table">
              <div className="flex items-center justify-between px-3 py-2 bg-slate-50 border-b border-slate-200">
                <div className="text-xs font-bold uppercase tracking-wide text-slate-700">
                  Shipping services {destCountry && <span className="font-normal normal-case text-slate-500">· ship to {destCountry}{svc?.origin_country ? ` from ${svc.origin_country}` : ""}</span>}
                </div>
                <div className="text-[11px] text-slate-500" title={svc?.price_note || ""}>💲 Price: on your Shopify Shipping bill after purchase</div>
              </div>
              {!svc ? (
                <div className="p-3 text-xs text-slate-500">Loading services…</div>
              ) : services.length === 0 ? (
                <div className="p-3 text-xs text-slate-500">No service catalogue for this destination — pick a carrier in Shopify Admin.</div>
              ) : (
                <table className="w-full text-xs">
                  <thead className="bg-white text-slate-500">
                    <tr className="[&>th]:px-2 [&>th]:py-1.5 [&>th]:text-left [&>th]:font-semibold border-b border-slate-100">
                      <th className="w-6"></th><th>Service</th><th>Delivery</th><th>Tracking</th><th>Signature</th><th>Insurance</th><th>Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {byCarrier.map(([ccode, c]) => (
                      <React.Fragment key={ccode}>
                        <tr className={`border-t border-slate-200 ${CARRIER_TONE[ccode] || "bg-slate-50"}`}>
                          <td className="px-2 py-1.5">
                            <input type="radio" name="svc" checked={isSel(ccode, null)} onChange={() => setSel({ carrier_code: ccode, service_code: null })} data-testid={`svc-${ccode}-any`} />
                          </td>
                          <td className="px-2 py-1.5 font-bold" colSpan={5}>{c.name} <span className="font-normal">· cheapest {c.name} service Shopify offers for this address</span></td>
                          <td className="px-2 py-1.5 text-slate-500">on bill</td>
                        </tr>
                        {c.rows.map((s) => (
                          <tr key={s.service_code} className={`border-t border-slate-100 hover:bg-emerald-50/40 cursor-pointer ${isSel(ccode, s.service_code) ? "bg-emerald-50" : ""}`}
                              onClick={() => setSel({ carrier_code: ccode, service_code: s.service_code })} data-testid={`svc-${ccode}-${s.service_code}`}>
                            <td className="px-2 py-1.5"><input type="radio" name="svc" checked={isSel(ccode, s.service_code)} onChange={() => setSel({ carrier_code: ccode, service_code: s.service_code })} /></td>
                            <td className="px-2 py-1.5"><div className="font-semibold text-slate-800">{s.service_name}</div>{s.tip && <div className="text-[10px] text-slate-500">{s.tip}</div>}</td>
                            <td className="px-2 py-1.5 text-slate-700">{s.transit}</td>
                            <td className="px-2 py-1.5">{s.tracking ? <span className="text-emerald-700 font-semibold">✓ included</span> : <span className="text-rose-700">✗ none</span>}</td>
                            <td className="px-2 py-1.5 text-slate-700">{s.signature}</td>
                            <td className="px-2 py-1.5 text-slate-700">{s.insurance}</td>
                            <td className="px-2 py-1.5 text-slate-500">on bill</td>
                          </tr>
                        ))}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Add-ons: only purchasable inside Shopify Admin */}
            <div className={`rounded-md border p-3 text-sm space-y-2 ${needsAdmin ? "border-amber-400 bg-amber-50" : "border-slate-200"}`} data-testid="shopify-addons">
              <div className="text-xs font-bold uppercase tracking-wide text-slate-700">Add-ons</div>
              <div className="flex flex-wrap gap-4">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={addons.signature} onChange={(e) => setAddons({ ...addons, signature: e.target.checked })} data-testid="addon-signature" />
                  ✍ Signature required
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={addons.insurance} onChange={(e) => setAddons({ ...addons, insurance: e.target.checked })} data-testid="addon-insurance" />
                  🛡 Insurance, declared value
                  <Input type="number" step="1" className="h-8 w-28" value={addons.insured_value} onChange={(e) => setAddons({ ...addons, insured_value: e.target.value })} disabled={!addons.insurance} data-testid="addon-insured-value" />
                  <span className="text-xs text-slate-500">{svc?.currency || ""}{svc?.order_total ? ` · order total ${svc.order_total}` : ""}</span>
                </label>
              </div>
              {needsAdmin ? (
                <div className="text-xs text-amber-900 space-y-2">
                  <div>{svc?.addons_note || "Signature and extra insurance can't be added through Shopify's API."}</div>
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" className="h-8 bg-slate-900 hover:bg-slate-800 text-white" disabled={!svc?.shopify_admin_order_url}
                      onClick={() => window.open(svc.shopify_admin_order_url, "_blank", "noopener")} data-testid="buy-in-shopify-admin">
                      🛍 Buy label in Shopify Admin ↗ {addons.signature ? "· signature" : ""}{addons.insurance ? ` · insure ${addons.insured_value || ""} ${svc?.currency || ""}` : ""}
                    </Button>
                    <Button size="sm" variant="outline" className="h-8" onClick={syncFromAdmin} disabled={saving} data-testid="sync-from-shopify">
                      {saving ? "Syncing…" : "⟳ Sync tracking from Shopify → Fulfilled"}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="text-[11px] text-slate-500">Every service above includes what its row says. Tick an add-on only if the customer needs more than that.</div>
              )}
            </div>

            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={!!form.notify_customer} onChange={(e) => setForm({ ...form, notify_customer: e.target.checked })} data-testid="shopify-label-notify" />
              Send Shopify's shipping-confirmation email with the tracking number
            </label>
            <UsmcaCheckbox order={order} checked={usmca} onChange={setUsmca} testid="shopify-usmca" />

            {/* Alternative: buy & print the label on the Shopify platform itself */}
            <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs space-y-2" data-testid="shopify-platform-row">
              <div className="font-bold uppercase tracking-wide text-slate-700">Or print the label on Shopify directly</div>
              <div className="text-slate-600">
                Opens this order in Shopify Admin — press <b>Create shipping label</b> there, pick the rate (prices shown), buy and print.
                Then come back and press <b>Sync tracking</b> so the CRM saves the tracking number and moves the order to Fulfilled.
              </div>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" className="h-8 bg-slate-900 hover:bg-slate-800 text-white" disabled={!svc?.shopify_admin_order_url}
                  onClick={() => window.open(svc.shopify_admin_order_url, "_blank", "noopener")} data-testid="open-in-shopify-admin">
                  🛍 Open order in Shopify Admin ↗
                </Button>
                <Button size="sm" variant="outline" className="h-8" onClick={syncFromAdmin} disabled={saving || !configured || !hasShopifyLink} data-testid="sync-from-shopify-main">
                  {saving ? "Syncing…" : "⟳ Sync tracking from Shopify → Fulfilled"}
                </Button>
              </div>
            </div>

            <div className="flex gap-2 justify-between pt-2 flex-wrap">
              <Button variant="ghost" size="sm" onClick={fulfillOnly} disabled={saving || !configured || !hasShopifyLink} className="text-slate-500"
                title="No label — just mark the items fulfilled in Shopify" data-testid="ship-shopify-fulfill-only">
                Mark fulfilled only (no label)
              </Button>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving} data-testid="ship-shopify-cancel">Cancel</Button>
                <Button onClick={submit} disabled={saving || !ready} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="ship-shopify-submit"
                  title={needsAdmin ? "Add-ons are bought in Shopify Admin — use the button above" : ""}>
                  {saving ? "Buying label…" : !configured ? "Not Connected" : !hasShopifyLink ? "No Shopify Link" : needsAdmin ? "Use Shopify Admin for add-ons" : "🛍 Buy label & print"}
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
